(() => {
  if (customElements.get('champion-fabric-guide')) return;
  let dataPromise;
  const node = (tag, text, className) => {
    const element = document.createElement(tag);
    if (text) element.textContent = text;
    if (className) element.className = className;
    return element;
  };
  const normalize = value => String(value).toLowerCase().replace(/[^a-z0-9.]/g, '');
  const match = (name, rows) => {
    const normalized = normalize(name);
    return rows.find(row => row.aliases.some(alias => normalized === normalize(alias) || normalized.endsWith(normalize(alias))));
  };
  class FabricGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-fabric-config]').textContent);
      this.labels = this.config.labels;
      const fabric = this.config.options.find(option => /^(fabric|material)$/i.test(option.name));
      this.names = fabric ? fabric.values.map(value => typeof value === 'string' ? value : value.name) : [];
      if (!this.names.length) {
        const found = this.config.description.match(/Fabric:\s*(.+?)(?:\.\s*(?:Choose|Contact)|\.\s*$)/i);
        if (found) this.names = [found[1].trim()];
      }
      this.trigger = this.querySelector('button');
      this.trigger.textContent = this.names.length > 1 ? this.labels.compare : this.names.length ? this.labels.view : this.labels.browse;
      this.trigger.hidden = false;
      this.trigger.addEventListener('click', () => this.open());
    }
    disconnectedCallback() {
      if (this.dialog) {
        this.dialog.close();
        this.dialog.remove();
      }
    }
    async open() {
      if (this.dialog) this.dialog.remove();
      const dialog = node('dialog', '', 'champion-fabric-dialog');
      this.dialog = dialog;
      dialog.setAttribute('aria-label', this.labels.title);
      const head = node('header', '', 'champion-fabric-head');
      head.append(node('h2', this.labels.title));
      const close = node('button', '×');
      close.setAttribute('aria-label', this.labels.close);
      close.title = this.labels.close;
      close.type = 'button';
      close.addEventListener('click', () => dialog.close());
      head.append(close);
      const body = node('div', '', 'champion-fabric-body');
      body.append(node('p', this.labels.loading));
      body.setAttribute('aria-live', 'polite');
      dialog.append(head, body);
      dialog.addEventListener('close', () => {
        dialog.querySelectorAll('video').forEach(video => video.pause());
        this.trigger.focus();
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      document.body.append(dialog);
      dialog.showModal();
      try {
        dataPromise ||= fetch(this.dataset.source).then(response => {
          if (!response.ok) throw new Error('Unavailable');
          return response.json();
        }).catch(error => { dataPromise = null; throw error; });
        const rows = await dataPromise;
        if (!dialog.isConnected) return;
        body.replaceChildren(node('p', this.labels.intro, 'champion-fabric-intro'));
        const nav = node('div', '', 'champion-fabric-nav');
        const grid = node('div', '', 'champion-fabric-grid');
        const display = (list, active) => {
          grid.querySelectorAll('video').forEach(video => video.pause());
          grid.replaceChildren(...list.map(row => this.card(row)));
          nav.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button === active)));
        };
        if (this.names.length) {
          const current = node('button', this.labels.product);
          current.type = 'button';
          current.addEventListener('click', () => display(this.names.map(name => {
            const record = match(name, rows);
            return record ? {...record, name} : {name, details:'', images:[], video:''};
          }), current));
          nav.append(current);
        }
        const all = node('button', this.labels.all);
        all.type = 'button';
        all.addEventListener('click', () => display(rows, all));
        nav.append(all);
        body.append(nav, grid);
        nav.firstElementChild.click();
      } catch (error) {
        body.replaceChildren(node('p', this.labels.error));
        const retry = node('button', this.labels.retry);
        retry.type = 'button';
        retry.addEventListener('click', () => { dialog.close(); this.open(); });
        body.append(retry);
      }
    }
    card(row) {
      const card = node('article', '', 'champion-fabric-card');
      card.append(node('h3', row.name));
      if (row.details) card.append(node('p', row.details));
      const media = node('div', '', 'champion-fabric-media');
      const photoGroup = node('div', '', 'champion-fabric-photo-group');
      if (row.images.length) {
        const photos = node('div', '', 'champion-fabric-photos');
        row.images.forEach((url, index) => {
          const caption = index === 0 ? this.labels.front : index === 1 ? this.labels.back : this.labels.detail;
          const figure = node('figure');
          const img = node('img');
          img.src = url;
          img.alt = row.name + ' — ' + caption;
          img.loading = 'lazy';
          img.width = 400;
          img.height = 400;
          img.addEventListener('error', () => figure.replaceChildren(node('p', this.labels.missing)), {once:true});
          const zoom = node('button', '', 'champion-fabric-photo');
          zoom.type = 'button';
          zoom.setAttribute('aria-label', this.labels.zoom + ': ' + row.name + ' — ' + caption);
          zoom.setAttribute('aria-pressed', 'false');
          const lens = node('span', '', 'champion-fabric-lens');
          lens.setAttribute('aria-hidden', 'true');
          lens.style.backgroundImage = 'url("' + url + '")';
          zoom.append(img, lens);
          zoom.addEventListener('click', () => {
            zoom.setAttribute('aria-pressed', String(zoom.getAttribute('aria-pressed') !== 'true'));
            lens.style.display = 'none';
          });
          zoom.addEventListener('pointermove', event => {
            if (event.pointerType !== 'mouse' || !img.naturalWidth || zoom.getAttribute('aria-pressed') === 'true') return;
            const rect = zoom.getBoundingClientRect();
            const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
            const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
            lens.style.display = 'block';
            const width = lens.offsetWidth;
            const height = lens.offsetHeight;
            const left = Math.max(0, Math.min(rect.width - width, x - width / 2));
            const top = Math.max(0, Math.min(rect.height - height, y - height / 2));
            lens.style.left = left + 'px';
            lens.style.top = top + 'px';
            const scale = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
            const imageWidth = img.naturalWidth * scale;
            const imageHeight = img.naturalHeight * scale;
            const imageX = x - (rect.width - imageWidth) / 2;
            const imageY = y - (rect.height - imageHeight) / 2;
            lens.style.backgroundSize = (imageWidth * 3) + 'px ' + (imageHeight * 3) + 'px';
            lens.style.backgroundPosition = (width / 2 - imageX * 3) + 'px ' + (height / 2 - imageY * 3) + 'px';
          });
          zoom.addEventListener('pointerleave', () => { lens.style.display = 'none'; });
          figure.append(zoom, node('figcaption', caption));
          photos.append(figure);
        });
        photoGroup.append(photos, node('p', this.labels.zoom_hint, 'champion-fabric-zoom-hint'));
        media.append(photoGroup);
      } else photoGroup.append(node('p', this.labels.missing));
      if (!row.images.length) media.append(photoGroup);
      if (row.video) {
        const video = node('video');
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';
        if (row.images[0]) video.poster = row.images[0];
        video.src = row.video;
        video.setAttribute('aria-label', row.name + ' — ' + this.labels.video);
        video.addEventListener('play', () => {
          this.dialog.querySelectorAll('video').forEach(other => { if (other !== video) other.pause(); });
        });
        media.append(video);
      }
      if (!row.video) media.classList.add('champion-fabric-media--photos-only');
      card.append(media);
      return card;
    }
  }
  customElements.define('champion-fabric-guide', FabricGuide);
})();


